import React, { useMemo } from 'react';
import { BentoCard, CategoryVocabularyMissingNotice, SkeletonBlock } from '../shared';
import { theme } from '../../theme';
import type { PrimaryAxisVolumeBin } from '../../services/api';
import { useCategoryMeta } from '../../context/ClassificationSchemaContext';

// <purpose>Per-category volume summary — sorted horizontal bar list showing total count per
// category for the active filter window. Replaces the old ECharts cumulative-line hero that
// suffered label collisions and hover dead-zones on VNB's 9 tie-prone marktprozess ids.</purpose>
// <dependencies>perPrimaryAxisVolumeByPeriod DTO field, useCategoryMeta resolver, BentoCard, theme</dependencies>
// <constraints>
//   VOLUME / RECONCILIATION surface — keys = cm.categoryKeys, unclassified bucket INCLUDED (the taxonomy the
//   statistics endpoint follows: PerPrimaryAxisVolumeByPeriod counts every classified ticket). The
//   categorization-performance surfaces (Kategorieverteilung, rates) use performanceCategoryKeys.
//   Sort: descending count; ties broken alphabetically by label (deterministic, no collision).
//   Labels via cm.label(key) — never raw ids.
//   Rows are <li> buttons for accessibility.
// </constraints>

type Props = {
  data: PrimaryAxisVolumeBin[];
  filterHash?: string;
};

export const VolumenJeKategorieCard: React.FC<Props> = ({ data }) => {
  const cm = useCategoryMeta();

  // Aggregate total count per category key across all bins.
  // This is the RECONCILIATION surface — cm.categoryKeys, unclassified bucket included. A row whose category
  // is not in the tenant's axis at all is still dropped by the knownKeys filter below, but the
  // unclassified value itself is a normal axis value here, not a category this card excludes.
  const rows = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const b of data) {
      totals[b.primaryAxis] = (totals[b.primaryAxis] ?? 0) + b.count;
    }
    // Keep only keys present in data that the tenant's axis knows about (union of data + axis keys).
    // Also include any key the axis exposes but with zero count (so gMSB always shows K0–K4).
    const knownKeys = new Set(cm.categoryKeys);
    const dataKeys = Object.keys(totals).filter(k => knownKeys.has(k));
    // Include axis keys not in data at count 0 so the full legend is always shown.
    const allKeys = Array.from(new Set([...dataKeys, ...cm.categoryKeys]));

    return allKeys
      .map(key => ({ key, label: cm.label(key), count: totals[key] ?? 0 }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      });
  }, [data, cm]);

  const maxCount = rows[0]?.count ?? 0;

  // Task C loading posture: the title itself glues cm.categoryNoun ("Volumen je Kategorie") —
  // untrustworthy while /config is still resolving (cm.configLoading), same placeholder-floor
  // risk as every other vocab-derived accessor below. Checked FIRST, ahead of vocabularyMissing
  // and the data-length empty state, since role/schema/vocab aren't resolved enough yet to trust
  // either of those branches' own conditions.
  if (cm.configLoading) {
    // Row count MUST track the real content's row count, not a fixed guess — a hardcoded 5 reads
    // fine for gMSB's 5 K-codes but undershoots the double-digit VNB marktprozess set, so the card
    // would grow taller the instant real data replaces the skeleton (UI Layout Stability Rule
    // violation). Reuses `rows` (computed above, unconditionally, every render) rather than
    // re-deriving categoryKeys a second time — it is ALREADY the exact allKeys the loaded branch
    // below renders, including the unclassified bucket and any out-of-vocab data keys, so this can only
    // ever match the real footprint, never merely approximate it. `rows` is empty (not merely
    // short) on a genuine cold boot — categoryKeys hasn't resolved either yet — so 5 is only that
    // cold-boot fallback.
    const skeletonRowCount = rows.length || 5;
    return (
      <BentoCard title={<SkeletonBlock width={140} height={14} />}>
        <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: skeletonRowCount }, (_, i) => 90 - (i % 4) * 15).map((pct, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SkeletonBlock width={10} height={10} borderRadius={2} />
              <SkeletonBlock width={140} height={12} />
              <SkeletonBlock width={`${pct}%`} height={8} borderRadius={3} style={{ flex: 1 }} />
              <SkeletonBlock width={28} height={12} />
            </li>
          ))}
        </ul>
      </BentoCard>
    );
  }

  // Role-configuration gap (no schema/vocabulary for a KNOWN non-legacy role) takes precedence
  // over the data-volume empty state below — otherwise `rows` silently comes out [] regardless of
  // `data.length` (every dataKey gets filtered out by an empty knownKeys set) and the card would
  // render an empty <ul> with no message at all, worse than a misleading "Keine Daten".
  if (cm.vocabularyMissing) {
    return (
      <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
        <CategoryVocabularyMissingNotice />
      </BentoCard>
    );
  }

  // Show empty state when there are no incoming data bins at all (not just all-zero schema keys).
  if (data.length === 0) {
    return (
      <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
        <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
          Keine Daten
        </div>
      </BentoCard>
    );
  }

  return (
    <BentoCard title={`Volumen je ${cm.categoryNoun}`}>
      <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(({ key, label, count }) => {
          const color = cm.color(key);
          const barWidthPct = maxCount > 0 ? (count / maxCount) * 100 : 0;
          return (
            <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Color swatch */}
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: color,
                }}
              />
              {/* Label */}
              <span
                style={{
                  flexShrink: 0,
                  width: 140,
                  fontSize: 12,
                  color: theme.colors.textSecondary,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
                title={label}
              >
                {label}
              </span>
              {/* Bar track */}
              <span
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 3,
                  background: theme.colors.bgLight,
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${barWidthPct}%`,
                    borderRadius: 3,
                    background: color,
                    // Transition width for smooth re-renders on filter change.
                    transition: 'width 300ms ease',
                  }}
                />
              </span>
              {/* Count */}
              <span
                style={{
                  flexShrink: 0,
                  width: 28,
                  textAlign: 'right',
                  fontSize: 12,
                  fontWeight: 600,
                  color: count > 0 ? color : theme.colors.textSecondary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {count}
              </span>
            </li>
          );
        })}
      </ul>
    </BentoCard>
  );
};
